import { makeStyles } from "@material-ui/styles";
import { useLinked } from "@type-r/react";
import { div, span } from "lqx-react";
import React, { useEffect } from "react";
import ReactDataSheet from "react-datasheet";
import { Cell } from "react-datasheet/lib";

const toLetters = (num: number) => {
    const mod = num % 26;
    let pow = num / 26 | 0;
    const out = mod ? String.fromCharCode(64 + mod) : (--pow, 'Z');
    return pow ? toLetters(pow) + out : out;
};

export interface GridElement extends ReactDataSheet.Cell<GridElement, string> {
    value: number | null;
}

function cloneHtmlElement(originalHtmlElement: HTMLElement) {
    const clone = originalHtmlElement.cloneNode() as HTMLElement;
    const { left, top, height } = originalHtmlElement.getBoundingClientRect();
    clone.style.position = 'absolute';
    clone.style.zIndex = '1000';
    clone.style.left = `${left}px`;
    clone.style.top = `${top}px`;
    clone.style.height = `${height}px`;
    document.body.append(clone);
    return clone;
}

export const DataSheet = ({ data, fontSize, onCellSelect, onComputeClasses, isViewMode = false, isFullScreen = false, isRunImportTemplateMode = false }:
    { data, fontSize, onCellSelect?, onComputeClasses?, isViewMode?, isFullScreen?, isRunImportTemplateMode?}) => {
    const classes = useStyles();
    const $columnWidths = useLinked(new Map<number, string>());
    const $data = useLinked([[]] as Cell<string, string>[][]);

    useEffect(() => {
        $data.set(reMap(data))
    }, [data]);

    const updateColumnWidth = (index: number, width: string) => $columnWidths.set(new Map($columnWidths.value.set(index, width)));

    const reMap = (arrays: any[]) => {
        const arrayRows = [];
        arrays.map((row, yIndex) => {
            arrayRows.push([yIndex + 1, ...row]
                .map((cell) => {
                    return { value: cell, readOnly: true, overflow: 'nowrap' };
                })
            );
        });

        const arrayLetters = [{ value: '', readOnly: true, className: '', overflow: 'nowrap' }];
        arrayRows[0].map((_: any, index: number) => {
            arrayLetters.push({ value: toLetters(index + 1), readOnly: true, className: 'system-cell', overflow: 'nowrap' });
        });

        return [arrayLetters, ...arrayRows];
    };

    function mouseDragHandler(event: React.MouseEvent<HTMLDivElement, MouseEvent>, columnIndex: number) {
        event.preventDefault();
        const dragElement = event.target as HTMLElement;
        const dragElementClone = cloneHtmlElement(dragElement);
        let xAxisPositionOffset = 0;


        const onMouseMove = (event: MouseEvent) => {
            xAxisPositionOffset += event.movementX;
            dragElementClone.style.left = `${event.pageX - dragElementClone.offsetWidth / 2}px`;
        }

        const onMouseUp = () => {
            const { width } = dragElement.parentElement.getBoundingClientRect();
            const newWidthPx = `${width + xAxisPositionOffset}px`;

            updateColumnWidth(columnIndex, newWidthPx);
            document.removeEventListener('mouseup', onMouseUp);
            document.removeEventListener('mousemove', onMouseMove);
            dragElementClone.remove();
        }

        document.addEventListener('mousemove', onMouseMove);
        document.addEventListener('mouseup', onMouseUp);
    }

    const rowRenderer: ReactDataSheet.RowRenderer<GridElement> = (props) => {
        const isHeaderRow = props.row === 0;

        const headerRow: ReactDataSheet.RowRenderer<GridElement> = (rowData) => <tr>{rowData.cells.map((cell, index) =>
            <td key={index} className="cell system-cell nowrap read-only" style={{ minWidth: $columnWidths.value.get(index), maxWidth: $columnWidths.value.get(index) }}>
                <TableHeaderText className="value-viewer">{cell.value}</TableHeaderText>
                <ColumnResizeHandle onMouseDown={(event) => mouseDragHandler(event, index)} ></ColumnResizeHandle>
            </td>
        )}
        </tr>

        const defaultRow: ReactDataSheet.RowRenderer<GridElement> = (rowData) => <tr>{rowData.children}</tr>;

        return isHeaderRow ? headerRow(props) : defaultRow(props);
    }

    const valueRenderer: ReactDataSheet.ValueRenderer<GridElement> = (cell) => cell.value;

    const cellRenderer = (props) => {
        const { row, col, cell, children, editing, updated, attributesRenderer, ...rest } = props;
        let classes = 'cell ';
        if (cell.readOnly) {
            classes = classes + 'read-only '
        }
        if (cell.overflow) {
            classes = classes + cell.overflow + ' '
        }
        if (row === 0) {
            classes = classes + 'system-cell '
        }
        if (onComputeClasses) {
            classes = onComputeClasses(col, row, classes);
        }

        cell.disableEvents = isViewMode;

        return (
            <td {...rest} className={classes}>
                {props.children}
            </td>
        );
    };

    return <>
        <DataSheetContainer className={isRunImportTemplateMode ? classes.dataViewModeSheetContainer : ''}>
            <TableContainer className={classes.sheetContainer} style={{ fontSize: fontSize + 'px', overflow: isFullScreen ? 'scroll' : 'visible' }}>
                <ReactDataSheet
                    data={$data.value}
                    rowRenderer={rowRenderer}
                    valueRenderer={valueRenderer}
                    cellRenderer={cellRenderer}
                    onSelect={({ start, end }) => {
                        if (onCellSelect) {
                            onCellSelect({ data: $data.value, start, end })
                        }
                    }}
                />
            </TableContainer>
        </DataSheetContainer>
    </>
}

const useStyles = makeStyles({
    sheetContainer: {
        '& .data-grid-container .data-grid .cell.read-only.system-cell': {
            backgroundColor: '#f0f0f0',
            color: 'black',
            textAlign: 'center',
            fontWeight: 500,
            paddingLeft: '10px',
            paddingRight: '10px'
        },
        '& .data-grid-container .data-grid .cell.read-only:first-child': {
            backgroundColor: '#f0f0f0'
        },
        '& .data-grid-container .data-grid .cell.read-only': {
            backgroundColor: 'white',
            color: 'black',
            fontWeight: 500
        },
        '& .data-grid-container .data-grid .cell.mapped': {
            border: '1px double rgb(33, 133, 208)'
        },
        '& .data-grid-container .data-grid .cell.selected': {
            border: '1px double #FFA500'
        },
        '& .data-grid-container .data-grid .cell:not(.system-cell)': {
            padding: '0px 10px 0px 10px'
        },
        '& .data-grid-container .data-grid tr': {
            lineHeight: 2
        }
    },
    dataViewModeSheetContainer: {
        '&': {
            height: 'calc(100% - 95px)',
            maxHeight: '25vh',
            minHeight: '25vh',
            overflow: 'auto',
        }
    }
});

const DataSheetContainer = div({
    height: 'calc(100% - 95px)'
});

const TableContainer = div({
    overflow: 'auto',
    maxHeight: '100%',
    flex: 1
});

const ColumnResizeHandle = div({
    display: 'inline-block',
    border: '3px solid transparent',
    height: '18px',
    width: '0',
    cursor: 'col-resize', float: 'right',
    '&:hover': {
        border: '3px solid #a1a1a1'
    }
});

const TableHeaderText = span({
    float: 'left',
    paddingLeft: '6px',
    width: 'calc(100% - 12px)'
});
